import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { Package, Eye, Truck, Search, ChevronDown, ChevronUp, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState({});
  const [supplierOrders, setSupplierOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [showSupplierOrders, setShowSupplierOrders] = useState(false);

  // Admin WhatsApp number for notifications
  const ADMIN_WHATSAPP = '+491716295816';

  useEffect(() => {
    loadOrders();
    loadSupplierOrders();
    loadCustomers();
  }, []);

  async function loadCustomers() {
    try {
      const response = await api.get('/admin/customers');
      const customerMap = {};
      (response.data || []).forEach(c => {
        customerMap[c.id] = c;
      });
      setCustomers(customerMap);
    } catch (error) {
      console.error('Failed to load customers', error);
    }
  }

  async function loadOrders() {
    try {
      const response = await api.get('/admin/orders');
      setOrders(response.data || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Bestellungen');
    } finally {
      setLoading(false);
    }
  }

  async function loadSupplierOrders() {
    try {
      const response = await api.get('/admin/supplier-orders');
      setSupplierOrders(response.data || []);
    } catch (error) {
      console.error('Failed to load supplier orders', error);
    }
  }

  async function updateOrderStatus(orderId, newStatus) {
    try {
      await api.put(`/admin/orders/${orderId}/status?status=${newStatus}`);
      setOrders(orders.map(o => o.id === orderId ? {...o, status: newStatus} : o));
      toast.success('Status aktualisiert');
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  }

  async function forwardToSupplier(orderId) {
    const order = orders.find(o => o.id === orderId);
    if (!order) return;

    try {
      const items = order.items.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        quantity: item.quantity,
        price: item.price,
        total: item.total
      }));

      const response = await api.post(`/admin/orders/${orderId}/forward-to-supplier`, items);
      toast.success(`An Fripac Medis weitergeleitet: ${response.data.fp_number}`);
      loadSupplierOrders();
    } catch (error) {
      toast.error('Fehler beim Weiterleiten');
    }
  }

  function sendWhatsAppNotification(order) {
    const customer = customers[order.user_id];
    const customerName = customer ? customer.company_name : 'Unbekannt';
    const customerContact = customer ? customer.contact_name : '';
    const orderDate = order.created_at ? new Date(order.created_at).toLocaleDateString('de-DE') : '';
    const statusLabel = statusOptions.find(s => s.value === order.status)?.label || order.status;
    const paymentInfo = order.payment_status === 'paid' ? 'Bezahlt' : order.payment_method === 'cash' ? 'Nachnahme' : 'Ausstehend';
    
    const itemsList = (order.items || []).map(item => 
      `• ${item.product_name} x${item.quantity} = €${(item.total || 0).toFixed(2)}`
    ).join('\n');

    const message = `🔔 *NEUE BESTELLUNG - ED STEEL*

📦 *Bestellnummer:* #${order.id.slice(0, 8).toUpperCase()}
📅 *Datum:* ${orderDate}

👤 *Kunde:* ${customerName}
${customerContact ? `📞 *Kontakt:* ${customerContact}` : ''}
📍 *Lieferadresse:* ${order.shipping_address || 'Nicht angegeben'}

🛒 *Artikel:*
${itemsList}

💰 *Gesamt:* €${(order.total || 0).toFixed(2)}
💳 *Zahlung:* ${paymentInfo}
📊 *Status:* ${statusLabel}

${order.notes ? `📝 *Anmerkungen:* ${order.notes}` : ''}`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${ADMIN_WHATSAPP.replace(/[^0-9]/g, '')}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  }

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (order.items || []).some(i => i.product_name.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statusOptions = [
    { value: 'eingegangen', label: 'Eingegangen', color: 'bg-blue-100 text-blue-800' },
    { value: 'in_bearbeitung', label: 'In Bearbeitung', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'versendet', label: 'Versendet', color: 'bg-purple-100 text-purple-800' },
    { value: 'geliefert', label: 'Geliefert', color: 'bg-green-100 text-green-800' }
  ];

  function getStatusColor(status) {
    return statusOptions.find(s => s.value === status)?.color || 'bg-gray-100 text-gray-800';
  }

  if (loading) {
    return <div className="text-center py-8">Wird geladen...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Package className="h-6 w-6" />
        <h2 className="text-2xl font-heading font-bold">Bestellungen ({orders.length})</h2>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="order-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="status-filter">
            <SelectValue placeholder="Status filtern" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            {statusOptions.map(s => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Orders List */}
      <div className="space-y-4">
        {filteredOrders.length === 0 ? (
          <div className="text-center py-8 text-gray-500">Keine Bestellungen gefunden</div>
        ) : (
          filteredOrders.map(order => (
            <div key={order.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`admin-order-${order.id}`}>
              {/* Order Header */}
              <div 
                className="p-4 flex flex-wrap justify-between items-center gap-4 cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
              >
                <div className="flex items-center gap-4">
                  {expandedOrder === order.id ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  <div>
                    <p className="font-semibold">#{order.id.slice(0, 8).toUpperCase()}</p>
                    <p className="text-sm text-gray-500">
                      {order.created_at ? new Date(order.created_at).toLocaleDateString('de-DE') : ''}
                    </p>
                    {customers[order.user_id] && (
                      <p className="text-sm font-medium text-primary">{customers[order.user_id].company_name}</p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                    {statusOptions.find(s => s.value === order.status)?.label || order.status}
                  </span>
                  <span className="font-bold">€{(order.total || 0).toFixed(2)}</span>
                  <span className={`px-2 py-1 rounded text-xs ${order.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                    {order.payment_status === 'paid' ? 'Bezahlt' : order.payment_method === 'cash' ? 'Nachnahme' : 'Ausstehend'}
                  </span>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedOrder === order.id && (
                <div className="border-t border-gray-200 p-4 bg-gray-50">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Order Items */}
                    <div>
                      <h4 className="font-semibold mb-2">Artikel</h4>
                      <div className="space-y-2">
                        {(order.items || []).map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span>{item.product_name} x{item.quantity}</span>
                            <span>€{(item.total || 0).toFixed(2)}</span>
                          </div>
                        ))}
                        <div className="border-t pt-2 mt-2 flex justify-between font-semibold">
                          <span>Gesamt</span>
                          <span>€{(order.total || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Customer & Shipping */}
                    <div>
                      <h4 className="font-semibold mb-2">Lieferadresse</h4>
                      <p className="text-sm text-gray-600">{order.shipping_address}</p>
                      {order.notes && (
                        <div className="mt-3">
                          <h4 className="font-semibold mb-1">Anmerkungen</h4>
                          <p className="text-sm text-gray-600">{order.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t">
                    <Select 
                      value={order.status} 
                      onValueChange={(value) => updateOrderStatus(order.id, value)}
                    >
                      <SelectTrigger className="w-[180px]" data-testid={`status-select-${order.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {statusOptions.map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => forwardToSupplier(order.id)}
                      data-testid={`forward-supplier-${order.id}`}
                    >
                      <Truck className="h-4 w-4 mr-2" />
                      An Fripac Medis
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => sendWhatsAppNotification(order)}
                      className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                      data-testid={`whatsapp-notify-${order.id}`}
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      WhatsApp
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Supplier Orders Section */}
      <div className="mt-8">
        <button
          className="flex items-center gap-2 text-lg font-semibold mb-4"
          onClick={() => setShowSupplierOrders(!showSupplierOrders)}
        >
          {showSupplierOrders ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          <Truck className="h-5 w-5" />
          Fripac Medis Bestellungen ({supplierOrders.length})
        </button>

        {showSupplierOrders && (
          <div className="space-y-3">
            {supplierOrders.length === 0 ? (
              <p className="text-gray-500">Keine Lieferantenbestellungen</p>
            ) : (
              supplierOrders.map(so => (
                <div key={so.id} className="bg-white border rounded-lg p-4" data-testid={`supplier-order-${so.id}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-blue-600">{so.fp_number}</p>
                      <p className="text-sm text-gray-500">Original: #{so.original_order_id.slice(0, 8).toUpperCase()}</p>
                      <p className="text-sm text-gray-500">
                        {so.created_at ? new Date(so.created_at).toLocaleDateString('de-DE') : ''}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${
                      so.status === 'geliefert' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {so.status}
                    </span>
                  </div>
                  <div className="mt-2 text-sm">
                    {(so.items || []).map((item, idx) => (
                      <div key={idx}>{item.product_name} x{item.quantity}</div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminOrders;
